import React, { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  MenuItem,
  TextField,
  Stack,
  Chip,
  InputAdornment,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ScreenRotationOutlinedIcon from "@mui/icons-material/ScreenRotationOutlined";
import dayjs from "dayjs";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { pageFieldSx } from "../../styles/pageModernStyles";

import TablaReducidaVista from "./TablaReducidaVista";
import TotalConPct from "./TotalConPct";
import { calcMargenMes, normalizeRepAlias } from "../../utils/margenesUtils";

const TablaReducida = ({
  resumenRows,
  year,
  setYear,
  empresa,
  setEmpresa,
}) => {
  const { isMinimized, contentMargin, isMobile } = useContext(SidebarContext);
  const isLandscape = useMediaQuery("(orientation: landscape)", {
    noSsr: true,
    defaultMatches: false,
  });
  const showRotatePrompt = isMobile && !isLandscape;
  const mobileLandscape = isMobile && isLandscape;

  const width = isMobile
    ? "100%"
    : isMinimized
      ? "calc(100% - 7em)"
      : "calc(100% - 17em)";
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const meses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const resumenGrouped = {};

  const [selectedData, setSelectedData] = useState(null);

  const [customerFilter, setCustomerFilter] = useState("");

  const filteredRows = resumenRows;

  filteredRows.forEach((row) => {
    const repAlias = normalizeRepAlias(row);
    const key = `${repAlias}__${row.CLIPRO}`;
    if (!resumenGrouped[key]) {
      resumenGrouped[key] = {
        REPALIAS: repAlias,
        CLIPRO: row.CLIPRO,
        meses: {},
      };
    }
    resumenGrouped[key].meses[row.MONTH] = {
      CLIPRO: row.CLIPRO,
      REPALIAS: row.REPALIAS,
      MONTH: row.MONTH,
      SITUACION: row.SITUACION,
      PedidoCompraE: row.PedidoCompraE,
      VentaBaseEUR: row.VentaBaseEUR,
      InvCostMoneda: row.InvCostMoneda,
      InvSalesMoneda: row.InvSalesMoneda,
    };
  });

  const resumenArray = Object.values(resumenGrouped);

  const normalizarTexto = (txt) =>
    String(txt || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const filtro = normalizarTexto(customerFilter);
  const filteredByCustomer = filtro
    ? resumenArray.filter((g) => {
        const customer = normalizarTexto(g.CLIPRO);
        const am = normalizarTexto(g.REPALIAS);
        return customer.includes(filtro) || am.includes(filtro);
      })
    : resumenArray;

  const margenMes = calcMargenMes;

  const margenTotalCliente = (g) =>
    meses.reduce((acc, mes) => acc + margenMes(g.meses[mes] || {}), 0);

  const reps = {};
  filteredByCustomer.forEach((g) => {
    if (!reps[g.REPALIAS]) reps[g.REPALIAS] = [];
    reps[g.REPALIAS].push(g);
  });

  const granTotalMeses = meses.reduce((acc, mes) => {
    acc[mes] = filteredByCustomer.reduce(
      (sum, g) => sum + margenMes(g.meses[mes] || {}),
      0,
    );
    return acc;
  }, {});
  const granTotalAnual = Object.values(granTotalMeses).reduce(
    (sum, val) => sum + val,
    0,
  );

  const totalClientes = filteredByCustomer.length;
  const totalReps = Object.keys(reps).length;

  const fieldSx = pageFieldSx(theme);

  if (showRotatePrompt) {
    return (
      <Box
        sx={{
          ...contentMargin,
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            maxWidth: 320,
            textAlign: "center",
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, isDark ? 0.35 : 0.08)}`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              animation: "tablaReducidaRotateHint 2.4s ease-in-out infinite",
              "@keyframes tablaReducidaRotateHint": {
                "0%, 100%": { transform: "rotate(0deg)" },
                "35%, 65%": { transform: "rotate(90deg)" },
              },
            }}
          >
            <ScreenRotationOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Gira el dispositivo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabla reducida está optimizada para móvil en horizontal. Rota el
            teléfono para ver la matriz de márgenes.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          ...contentMargin,
          boxSizing: "border-box",
          ...(mobileLandscape
            ? {
                width: "100%",
                height: "100dvh",
                maxHeight: "100dvh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                p: 0.75,
                mt: 0,
              }
            : {
                mt: 2,
                width,
                pl: 3,
              }),
        }}
      >
        {/* 🔹 Hero header fuera del scroll de la tabla */}
        <Box
          sx={{
            flexShrink: 0,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              isDark ? 0.18 : 0.08,
            )} 0%, ${alpha(
              theme.palette.background.paper,
              0.96,
            )} 60%, ${theme.palette.background.paper} 100%)`,
            border: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: mobileLandscape ? 10 : 12,
            borderTopRightRadius: mobileLandscape ? 10 : 12,
            px: mobileLandscape ? 1 : { xs: 1.5, md: 2 },
            py: mobileLandscape ? 0.75 : 1.25,
          }}
        >
          <Stack
            direction={mobileLandscape ? "row" : { xs: "column", md: "row" }}
            spacing={mobileLandscape ? 1 : 1.5}
            alignItems={mobileLandscape ? "center" : { xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            {/* Identidad */}
            <Stack direction="row" spacing={mobileLandscape ? 1 : 1.5} alignItems="center">
              <Box
                sx={{
                  width: mobileLandscape ? 36 : 44,
                  height: mobileLandscape ? 36 : 44,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: theme.palette.primary.contrastText,
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.45)}`,
                  flexShrink: 0,
                }}
              >
                <AssessmentOutlinedIcon fontSize={mobileLandscape ? "small" : "medium"} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                {!mobileLandscape ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label="CRM"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        border: "1px solid",
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      }}
                    />
                    <Chip
                      label={`${totalReps} AM · ${totalClientes} clientes`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        backgroundColor: alpha(theme.palette.text.primary, 0.06),
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Stack>
                ) : null}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    letterSpacing: -0.2,
                    color: theme.palette.text.primary,
                    fontSize: mobileLandscape
                      ? "0.95rem"
                      : { xs: "1.05rem", md: "1.25rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  Anual Margin by Client{" "}
                  <Box
                    component="span"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    · {year}
                  </Box>
                </Typography>
                {mobileLandscape ? (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalReps} AM · {totalClientes} clientes · desliza horizontalmente
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            {/* Filtros */}
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                flexShrink: 0,
                flexWrap: mobileLandscape ? "nowrap" : "wrap",
                width: mobileLandscape ? "auto" : { xs: "100%", sm: "auto" },
              }}
            >
              <TextField
                size="small"
                placeholder={mobileLandscape ? "Buscar…" : "Buscar cliente o AM…"}
                variant="outlined"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                sx={{
                  ...fieldSx,
                  minWidth: mobileLandscape ? 120 : { xs: "100%", sm: 240 },
                  maxWidth: mobileLandscape ? 160 : undefined,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                sx={{
                  ...fieldSx,
                  minWidth: mobileLandscape ? 108 : 150,
                }}
                InputProps={{
                  startAdornment: mobileLandscape ? undefined : (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="stage">STAGE</MenuItem>
                <MenuItem value="erna">ERNA</MenuItem>
                <MenuItem value="fusion">
                  {mobileLandscape ? "TOTAL" : "TOTAL (STAGE+ERNA)"}
                </MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{
                  ...fieldSx,
                  minWidth: mobileLandscape ? 84 : 120,
                }}
                InputProps={{
                  startAdornment: mobileLandscape ? undefined : (
                    <InputAdornment position="start">
                      <CalendarMonthOutlinedIcon
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                }}
              >
                {[...Array(6)].map((_, i) => {
                  const y = dayjs().year() - i;
                  return (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
          </Stack>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            flex: mobileLandscape ? 1 : undefined,
            minHeight: mobileLandscape ? 0 : undefined,
            height: mobileLandscape ? "auto" : "calc(100vh - 120px)",
            pr: mobileLandscape ? 0 : 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            border: "1px solid",
            borderTop: 0,
            borderColor: "divider",
            borderBottomLeftRadius: mobileLandscape ? 10 : 12,
            borderBottomRightRadius: mobileLandscape ? 10 : 12,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Table
            size="small"
            stickyHeader={!mobileLandscape}
            sx={{
              "& .MuiTableCell-root": {
                borderColor: alpha(theme.palette.divider, 0.6),
              },
            }}
          >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  minWidth: 160,
                  maxWidth: 160,
                }}
              >
                Account Manager
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 160,
                  top: 0,
                  zIndex: 4,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  minWidth: 200,
                  maxWidth: 200,
                }}
              >
                Customer
              </TableCell>
              {meses.map((mes) => (
                <TableCell
                  key={mes}
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      isDark ? 0.12 : 0.06,
                    ),
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  {dayjs()
                    .month(mes - 1)
                    .format("MMM")}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.primary.contrastText,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  minWidth: 168,
                }}
              >
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(reps).map(([rep, clientes]) => (
              <React.Fragment key={rep}>
                {clientes.map((g, i) => {
                  const stickyBg =
                    theme.palette.estimates?.main ??
                    alpha(theme.palette.text.primary, isDark ? 0.06 : 0.03);
                  const repAlias = (g.REPALIAS || "").toString().trim();
                  const hasRep = repAlias.length > 0;
                  return (
                    <TableRow
                      key={i}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            isDark ? 0.08 : 0.04,
                          ),
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: stickyBg,
                          zIndex: 1,
                          minWidth: 160,
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: hasRep
                            ? theme.palette.text.secondary
                            : theme.palette.warning.main,
                          fontStyle: hasRep ? "normal" : "italic",
                        }}
                      >
                        {hasRep ? repAlias : "— Sin AM —"}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 160,
                          backgroundColor: stickyBg,
                          zIndex: 1,
                          minWidth: 300,
                          maxWidth: 300,
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {g.CLIPRO}
                      </TableCell>
                      {meses.map((mes) => {
                        const m = g.meses[mes] || {};
                        const margen = margenMes(m);
                        const isAbierto =
                          (m.SITUACION || "").trim().toUpperCase() !== "S" ||
                          (m.ESTADO || "").trim().toUpperCase() === "A";
                        const cellColor = isAbierto
                          ? theme.palette.info.main
                          : theme.palette.success.main;
                        const hasValue = margen !== 0;

                        return (
                          <TableCell
                            key={mes}
                            align="right"
                            sx={{ p: 0.5 }}
                          >
                            <Box
                              onClick={() =>
                                setSelectedData({
                                  ...m,
                                  CLIPRO: g.CLIPRO,
                                  REPALIAS: g.REPALIAS,
                                })
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0.5,
                                borderRadius: 1.25,
                                minWidth: "6.5em",
                                cursor: "pointer",
                                border: "1px solid",
                                borderColor: hasValue
                                  ? alpha(cellColor, 0.25)
                                  : "transparent",
                                backgroundColor: hasValue
                                  ? alpha(cellColor, isDark ? 0.12 : 0.06)
                                  : "transparent",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                  backgroundColor: alpha(
                                    cellColor,
                                    isDark ? 0.22 : 0.14,
                                  ),
                                  borderColor: alpha(cellColor, 0.5),
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: hasValue
                                    ? cellColor
                                    : theme.palette.text.disabled,
                                  fontSize: "0.78rem",
                                }}
                              >
                                {hasValue
                                  ? margen.toLocaleString("es-ES", {
                                      maximumFractionDigits: 2,
                                    })
                                  : "—"}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            isDark ? 0.1 : 0.05,
                          ),
                        }}
                      >
                        <TotalConPct
                          valor={margenTotalCliente(g)}
                          referencia={granTotalAnual}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Totales por REPALIAS */}
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      letterSpacing: 0.4,
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                      backgroundImage: `linear-gradient(${alpha(
                        theme.palette.warning.main,
                        isDark ? 0.16 : 0.1,
                      )}, ${alpha(
                        theme.palette.warning.main,
                        isDark ? 0.16 : 0.1,
                      )})`,
                      borderTop: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                      borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                    }}
                  >
                    Σ Totales · {rep}
                  </TableCell>

                  {(() => {
                    let totalAnualRep = 0;
                    return (
                      <>
                        {meses.map((mes) => {
                          const totalMesRep = clientes.reduce(
                            (acc, g) => acc + margenMes(g.meses[mes] || {}),
                            0,
                          );
                          totalAnualRep += totalMesRep;
                          return (
                            <TableCell
                              key={mes}
                              align="center"
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                backgroundColor: alpha(
                                  theme.palette.warning.main,
                                  isDark ? 0.14 : 0.08,
                                ),
                                borderTop: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                                borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                              }}
                            >
                              {totalMesRep.toLocaleString("es-ES")}
                            </TableCell>
                          );
                        })}
                        <TableCell
                          align="center"
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 800,
                            color: theme.palette.primary.contrastText,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          }}
                        >
                          <TotalConPct
                            valor={totalAnualRep}
                            referencia={granTotalAnual}
                            variant="inverted"
                          />
                        </TableCell>
                      </>
                    );
                  })()}
                </TableRow>
              </React.Fragment>
            ))}

            {/* Gran Total */}
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  letterSpacing: 0.4,
                  color: theme.palette.primary.contrastText,
                  backgroundColor: theme.palette.primary.dark,
                  backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                }}
              >
                ◆ Gran Total · todas las AM
              </TableCell>
              {meses.map((mes) => (
                <TableCell
                  key={`grand-${mes}`}
                  align="center"
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    color: theme.palette.primary.contrastText,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  }}
                >
                  {granTotalMeses[mes].toLocaleString("es-ES")}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 800,
                  color: theme.palette.primary.contrastText,
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
                  boxShadow: `inset 0 0 0 1px ${alpha(
                    theme.palette.common.white,
                    0.15,
                  )}`,
                }}
              >
                {granTotalAnual.toLocaleString("es-ES")}
              </TableCell>
            </TableRow>
          </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 🔹 Dialog detalle */}
      <Dialog
        open={!!selectedData}
        onClose={() => setSelectedData(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: `0 24px 64px ${alpha(
              theme.palette.common.black,
              isDark ? 0.5 : 0.18,
            )}`,
          },
        }}
      >
        {selectedData && (
          <>
            <DialogTitle
              sx={{
                p: 0,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.22 : 0.1,
                )} 0%, ${theme.palette.background.paper} 100%)`,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ p: 2, pr: 6 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: theme.palette.primary.contrastText,
                      flexShrink: 0,
                    }}
                  >
                    <AssessmentOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip
                        label={dayjs()
                          .month(selectedData.MONTH - 1)
                          .format("MMMM")
                          .toUpperCase()}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.15,
                          ),
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Chip
                        label={year}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          backgroundColor: alpha(
                            theme.palette.text.primary,
                            0.08,
                          ),
                          color: theme.palette.text.secondary,
                        }}
                      />
                    </Stack>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1.05rem",
                        lineHeight: 1.2,
                        mt: 0.25,
                        color: theme.palette.text.primary,
                      }}
                      noWrap
                    >
                      {selectedData.CLIPRO}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                      }}
                    >
                      Account Manager: {selectedData.REPALIAS}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                aria-label="close"
                onClick={() => setSelectedData(null)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.text.secondary,
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.error.main, 0.12),
                    color: theme.palette.error.main,
                  },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <TablaReducidaVista m={selectedData} />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default TablaReducida;
